"use client";

import { useEffect, useState } from "react";
import { getZodiacSign } from "@/lib/zodiac";
import { TarotRequestCreate } from "@/types/tarot";
import { useI18n } from "@/i18n/I18nProvider";
// import { trackInternalFunnelEvent } from "@/services/analytics";

function formatStoredBirthDate(iso: string | undefined, language: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || "").trim());
  if (!match) return "";
  const [, year, month, day] = match;
  return language === "en-US" ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
}

interface Step2QuerentProps {
  formData: TarotRequestCreate;
  fieldErrors: Set<string>;
  onInputChange: (
    field: string,
    value: string | boolean | Record<string, string | boolean> | null,
  ) => void;
  onFieldErrorRemove: (field: string) => void;
}

export function Step2Querent({
  formData,
  fieldErrors,
  onInputChange,
  onFieldErrorRemove,
}: Step2QuerentProps) {
  const { t, lang } = useI18n();
  const birthDateIso = (
    formData.querent as unknown as { birth_date?: string }
  ).birth_date;
  const [birthDate, setBirthDate] = useState(() =>
    formatStoredBirthDate(birthDateIso, lang),
  );
  const [dateError, setDateError] = useState<string>("");

  // pt-BR e pt-PT usam DD/MM/AAAA; apenas en-US usa MM/DD/YYYY.
  const usesDayFirstDate = lang !== "en-US";
  const datePlaceholder = usesDayFirstDate ? "DD/MM/AAAA" : "MM/DD/YYYY";
  const [dateSource, setDateSource] = useState({ birthDateIso, lang });
  if (birthDateIso !== dateSource.birthDateIso || lang !== dateSource.lang) {
    setDateSource({ birthDateIso, lang });
    const iso = (birthDateIso || "").trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (match) {
      const [, year, month, day] = match;
      setBirthDate(usesDayFirstDate
        ? `${day}/${month}/${year}`
        : `${month}/${day}/${year}`);
    }
  }

  const formatDateInput = (value: string): string => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  };

  const convertToISODate = (formattedDate: string): string => {
    const numbers = formattedDate.replace(/\D/g, "");
    if (numbers.length !== 8) return "";

    let day: string, month: string, year: string;
    if (usesDayFirstDate) {
      day = numbers.slice(0, 2);
      month = numbers.slice(2, 4);
      year = numbers.slice(4, 8);
    } else {
      month = numbers.slice(0, 2);
      day = numbers.slice(2, 4);
      year = numbers.slice(4, 8);
    }

    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    if (monthNum < 1 || monthNum > 12) return "";
    if (dayNum < 1 || dayNum > 31) return "";
    if (yearNum < 1900 || yearNum > new Date().getFullYear()) return "";

    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (
      date.getFullYear() !== yearNum ||
      date.getMonth() !== monthNum - 1 ||
      date.getDate() !== dayNum
    ) {
      return "";
    }

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (birthDateIso && !formData.querent.zodiac_sign) {
      const zodiacSign = getZodiacSign(birthDateIso, t);
      if (zodiacSign) onInputChange("querent.zodiac_sign", zodiacSign);
    }
  }, [birthDateIso, formData.querent.zodiac_sign, onInputChange, t]);

  return (
    <div className="space-y-3">
      {/* <div className="bg-white/50 rounded-lg p-3 border border-[rgba(232,115,115,0.2)] mb-3">
        <div className="flex items-center space-x-2">
          <div className="text-xl">🙋‍♀️</div>
          <p className="form-description-text">
            <span className="font-title">
              {t("step2.header.title", "Vamos começar pela sua energia:")}
            </span>{" "}
            {t(
              "step2.header.desc",
              "Informe seu primeiro nome para iniciar sua leitura.",
            )}
          </p>
        </div>
      </div> */}

      <div>
        <label className="form-label-compact">
          {t("step2.full_name.label", "Seu nome *")}
        </label>
        <input
          type="text"
          value={formData.querent.full_name}
          onFocus={() => {
            // void trackInternalFunnelEvent("StepSobreVoceNameFocused", {
            //   stepNumber: 1,
            //   stepLabel: "sobre_voce",
            //   maxSteps: 5,
            // });
          }}
          onChange={(e) => {
            onInputChange("querent.full_name", e.target.value);
            if (fieldErrors.has("querent.full_name")) {
              onFieldErrorRemove("querent.full_name");
            }
          }}
          className={`form-input-compact w-full focus:outline-none transition-all duration-300 ${
            fieldErrors.has("querent.full_name")
              ? "border-red-500 animate-pulse focus:border-red-600"
              : "focus:border-[rgba(232,115,115,0.5)]"
          }`}
          placeholder={t("step2.full_name.placeholder", "Ex: Maria Silva")}
        />
        {fieldErrors.has("querent.full_name") && (
          <div className="mt-1 flex items-center text-red-600 text-xs font-medium">
            <span className="mr-1">⚠️</span>
            {t("common.error.required", "Campo obrigatório")}
          </div>
        )}
      </div>

      <div>
        <label className="form-label-compact">
          {t("step2.birth_date.label", "Sua data de nascimento *")}
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={birthDate}
          onFocus={() => {
            // void trackInternalFunnelEvent("StepSobreVoceBirthDateFocused", {
            //   stepNumber: 1,
            //   stepLabel: "sobre_voce",
            //   maxSteps: 5,
            // });
          }}
          onChange={(e) => {
            const formatted = formatDateInput(e.target.value);
            setBirthDate(formatted);
            setDateError("");

            const numbers = formatted.replace(/\D/g, "");
            if (numbers.length === 8) {
              const isoDate = convertToISODate(formatted);
              if (isoDate) {
                onInputChange("querent.birth_date", isoDate);
                const zodiacSign = getZodiacSign(isoDate, t);
                if (zodiacSign) onInputChange("querent.zodiac_sign", zodiacSign);
              } else {
                setDateError(
                  t(
                    "step2.birth_date.invalid",
                    "Data inválida. Verifique o dia, mês e ano.",
                  ),
                );
                onInputChange("querent.birth_date", "");
                onInputChange("querent.zodiac_sign", "");
              }
            } else {
              onInputChange("querent.birth_date", "");
              onInputChange("querent.zodiac_sign", "");
            }

            if (fieldErrors.has("querent.birth_date")) {
              onFieldErrorRemove("querent.birth_date");
            }
          }}
          placeholder={datePlaceholder}
          maxLength={10}
          className={`form-input-compact w-full focus:outline-none transition-all duration-300 ${
            fieldErrors.has("querent.birth_date") || dateError
              ? "border-red-500 animate-pulse focus:border-red-600"
              : "focus:border-[rgba(232,115,115,0.5)]"
          }`}
        />
        {fieldErrors.has("querent.birth_date") && (
          <div className="mt-1 flex items-center text-red-600 text-xs font-medium">
            <span className="mr-1">⚠️</span>
            {t("common.error.required", "Campo obrigatório")}
          </div>
        )}
        {dateError && (
          <div className="mt-1 flex items-center text-red-600 text-xs font-medium">
            <span className="mr-1">⚠️</span>
            {dateError}
          </div>
        )}
      </div>
    </div>
  );
}
